import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { filter, isEqual } from 'lodash-es'
import { v4 } from 'uuid'
import { Stack } from '@mui/material'
import CreateElementLabelField from '@editor/pages/StructuralPlanning/components/CreateElementLabelField'
import {
  applyCreateLabel,
  createLabelSchema,
} from '@editor/pages/StructuralPlanning/components/RectangularShape/components/RectangularShapeForm/components/GeometryForm/schema'
import { HotkeyDeleteButton, HotkeySaveButton, SaveButton } from '@ui/actions'
import { Form, Errors } from '@ui/forms'
import LineGeometryFormFields from '@ui/forms/LineGeometryForm/formFields'
import { useModelStore } from '@editorStores'
import { DefaultValues, xyBeamSchema, xyDiagonalBeamSchema } from '../schemas'

interface Props {
  handleSubmit: (beam: PlanLineElement) => void
  handleDelete: () => void
  beam: PlanLineElement
  interactionMode: '2D' | '3D'
  geometryType: 'xy' | 'xy_diagonal'
  isDeleting?: boolean
  isSubmitting?: boolean
  showCreateLabel?: boolean
  geometryTypeToggle?: ReactNode
}

const GeometryForm = ({
  beam,
  handleSubmit,
  handleDelete,
  geometryType,
  interactionMode,
  isDeleting = false,
  isSubmitting,
  showCreateLabel = false,
  geometryTypeToggle,
}: Props): ReactElement | null => {
  const { beams } = useModelStore(state => state.model)
  const updateBeam = useModelStore(state => state.updateBeam)

  const beamsInStorey = useMemo(() => {
    return filter(beams, ['storey', beam.storey])
  }, [beam.storey, beams])

  const defaultValues = useMemo<DefaultValues>(() => {
    const { start } = beam.shape
    const { end } = beam.shape

    return {
      guid: beam.guid,
      start,
      end,
      isIntersecting: false,
      ...(showCreateLabel ? { label: '' } : {}),
    }
  }, [beam, showCreateLabel])

  const validationSchema = useMemo(() => {
    const geometrySchema = (
      geometryType === 'xy_diagonal' ? xyDiagonalBeamSchema : xyBeamSchema
    ) as typeof xyBeamSchema
    return showCreateLabel ? geometrySchema.concat(createLabelSchema) : geometrySchema
  }, [geometryType, showCreateLabel])

  const validationContext = {
    beams: beamsInStorey,
  }

  // this is necessary so that the form state is reset when the form is submitted
  const [formStateRandomId, setFormStateRandomId] = useState(v4())

  const SubmitButton = showCreateLabel ? HotkeySaveButton : SaveButton

  return (
    <Form
      key={`${beam.guid}-${formStateRandomId}`}
      onSubmit={state => {
        handleSubmit(showCreateLabel ? applyCreateLabel(beam, state.label) : beam)
        setFormStateRandomId(v4())
      }}
      validationSchema={validationSchema}
      validationContext={validationContext}
      defaultValues={defaultValues}
      data-cy="beam-geometry-form"
      style={{ width: '100%' }}
    >
      <Stack direction="column" spacing={1}>
        <FormValueUpdater beam={beam} />
        {showCreateLabel && <CreateElementLabelField />}
        {geometryTypeToggle}
        <LineGeometryFormFields
          updateElementMutation={updateBeam}
          interactionMode={interactionMode}
        />
      </Stack>

      <Errors />

      <Stack direction="row" spacing={1}>
        <SubmitButton
          type="submit"
          loading={isSubmitting}
          fullWidth
          disabled={isDeleting}
          data-cy="btn-submit"
          size="small"
        >
          Speichern
        </SubmitButton>
        <HotkeyDeleteButton
          onClick={handleDelete}
          size="small"
          fullWidth
          loading={isDeleting}
          disabled={isSubmitting}
          data-cy="delete-beam-btn"
        >
          Löschen
        </HotkeyDeleteButton>
      </Stack>
    </Form>
  )
}

// Component to update form values when beam geometry changes
const FormValueUpdater = ({ beam }: { beam: ShapeObjectLine }): null => {
  const { setValue, getValues } = useFormContext()

  useEffect(() => {
    const formValues = getValues()

    const { start, end } = beam.shape

    const shapeStart = { x: start.x, y: start.y, z: start.z }
    const shapeEnd = { x: end.x, y: end.y, z: end.z }

    const formStart = formValues.start
    const formEnd = formValues.end

    if (isEqual(shapeStart, formStart) && isEqual(shapeEnd, formEnd)) return

    // Update form field values without changing default values
    setValue('start', shapeStart, { shouldDirty: false })

    setValue('end', shapeEnd, { shouldDirty: false })
  }, [beam.shape, setValue, getValues])

  return null
}

export default GeometryForm
